import { useQuery } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';

import queryString from 'query-string';
import { ENV } from '@shared/config';
import type { TBasketResponse } from '@shared/types';
import { decodeHtmlEntities, withApiErrorLogging } from '@shared/utils';
import { getCurrency } from '../../../app/currency';
// eslint-disable-next-line import/no-internal-modules
import { getClientToken } from '../../libs/auth-context';

import { BasketQueryKey } from './keys';

/**
 * Улучшенный хук для получения данных корзины с расширенной обработкой ошибок
 * и оптимизированной стратегией обновления данных
 */
export function useGetBasketQuery() {
  return useQuery<TBasketResponse>(
    [BasketQueryKey],
    async () => {
      return withApiErrorLogging(async () => {
        const token = getClientToken();

        // eslint-disable-next-line @typescript-eslint/await-thenable
        const currency = await getCurrency();

        const requestParams = {
          lang: ENV.LANG,
          token,
          currency: currency?.value || ENV.DEFAULT_CERRENCY,
        };

        const requestLink = `${ENV.API}/basket/?${queryString.stringify(
          requestParams,
          {
            skipEmptyString: true,
          },
        )}`;

        // Отправляем информацию о запросе в Telegram для отладки
        if (process.env.NODE_ENV === 'development') {
          try {
            await fetch('/api-v2/send-log', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                level: 1, // Информационный уровень
                msg: JSON.stringify({
                  info: 'Basket Request Debug',
                  url: requestLink,
                  params: requestParams,
                  token: token ? 'present' : 'missing',
                  timestamp: new Date().toISOString(),
                }),
                href: typeof window !== 'undefined' ? window.location.href : '',
                titlePage: typeof document !== 'undefined' ? document.title : '',
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
              }),
            });
          } catch (logError) {
            // eslint-disable-next-line no-console
            console.error('Ошибка отправки debug лога в Telegram:', logError);
          }
        }

        try {
          const { data } = await axios.get<unknown, TBasketResponse>(requestLink, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            timeout: 15000, // 15 секунд таймаут
            // Добавляем retry на уровне axios
            validateStatus: (status) => status < 500, // Не считаем 4xx ошибки за сетевые
          });

          return decodeHtmlEntities(data as unknown as TBasketResponse);
        } catch (error) {
          // Дополнительная обработка специфичных ошибок
          if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;
            
            // Отправляем детали ошибки в Telegram
            try {
              await fetch('/api-v2/send-log', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  level: 3,
                  msg: JSON.stringify({
                    error: 'Basket API Error',
                    message: axiosError.message,
                    code: axiosError.code,
                    status: axiosError.response?.status,
                    statusText: axiosError.response?.statusText,
                    url: requestLink,
                    isNetworkError: !axiosError.response,
                    requestParams,
                    timestamp: new Date().toISOString(),
                  }),
                  href: typeof window !== 'undefined' ? window.location.href : '',
                  titlePage: typeof document !== 'undefined' ? document.title : '',
                  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                }),
              });
            } catch (logError) {
              // eslint-disable-next-line no-console
              console.error('Ошибка отправки лога в Telegram:', logError);
            }

            // Специальная обработка для сетевых ошибок
            if (!axiosError.response) {
              throw new Error(`Сетевая ошибка при загрузке корзины: ${axiosError.message}`);
            }

            // Обработка HTTP ошибок
            if (axiosError.response.status === 401) {
              throw new Error('Ошибка авторизации при загрузке корзины');
            }
            
            if (axiosError.response.status === 403) {
              throw new Error('Доступ запрещен при загрузке корзины');
            }
            
            if (axiosError.response.status >= 500) {
              throw new Error('Ошибка сервера при загрузке корзины');
            }
          }
          
          throw error;
        }
      }, '/basket/', 'GET');
    },
    {
      // Оптимизированная стратегия обновления данных
      refetchOnWindowFocus: 'always',
      refetchOnMount: 'always',
      refetchOnReconnect: true,
      
      // Настройки повторных попыток
      retry: (failureCount, error) => {
        // Не повторяем для клиентских ошибок (4xx)
        if (axios.isAxiosError(error) && error.response?.status && error.response.status < 500) {
          return false;
        }
        
        // Максимум 3 попытки для сетевых ошибок
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Экспоненциальная задержка
      
      // Настройки кеширования
      staleTime: 1 * 1000, // 1 секунда - данные считаются свежими
    }
  );
}
